import { AlertTriangle, Check, FileSpreadsheet, Sparkles, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { sectionCls } from '@/pages/launch/helpers';

interface LaunchOutcomesSectionProps {
  sectionRef: React.Ref<HTMLDivElement>;
  visible: boolean;
}

const LaunchOutcomesSection = ({ sectionRef, visible }: LaunchOutcomesSectionProps) => (
  <section
    id="phase1"
    ref={sectionRef}
    className={`py-12 sm:py-20 px-4 sm:px-6 bg-muted/30 ${sectionCls(visible)}`}
  >
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-14">
        <Badge className="mb-4 bg-success/10 text-success border-success/30 hover:bg-success/15">
          The Impact
        </Badge>
        <h2 className="text-3xl sm:text-4xl font-display font-bold">
          What Changes When Your <span className="gradient-text">Data Talks.</span>
        </h2>
        <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
          Every overpoured drink, missed VIP, and blind booking decision has a price tag. Here's
          what happens when you can finally see it.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <SaveHoursCard />
        <RevenueLeaksCard />
        <BookConfidenceCard />
      </div>
    </div>
  </section>
);

const SaveHoursCard = () => (
  <Card className="p-0 overflow-hidden group hover:shadow-xl transition-all hover:-translate-y-1 duration-300 border-border/60 flex flex-col">
    <div className="relative p-5 bg-gradient-to-br from-primary/8 via-primary/3 to-transparent overflow-hidden h-[220px] flex flex-col justify-between">
      <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] bg-success/15 text-success px-2 py-0.5 rounded-full font-semibold">
        <Check size={10} /> Auto
      </div>
      <div className="space-y-2 pt-1">
        {[
          { label: 'Settlement Report', time: '0:04' },
          { label: 'P&L Breakdown', time: '0:12' },
          { label: 'Performance Summary', time: '0:08' },
        ].map((r) => (
          <div
            key={r.label}
            className="flex items-center gap-2.5 bg-background/80 backdrop-blur-sm rounded-lg px-3 py-2 border border-border/30"
          >
            <div className="h-5 w-5 rounded-full bg-success/15 flex items-center justify-center flex-shrink-0">
              <Check size={10} className="text-success" />
            </div>
            <span className="text-xs font-medium text-foreground flex-1">{r.label}</span>
            <span className="text-[10px] text-muted-foreground font-mono">{r.time}s</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-1">
        <span className="text-[10px] text-muted-foreground">3 reports generated</span>
        <span className="text-[10px] font-bold text-success">10+ hrs saved/week</span>
      </div>
    </div>
    <div className="p-5 border-t border-border/40 flex-1 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <FileSpreadsheet size={14} className="text-primary" />
        </div>
        <p className="font-semibold text-sm">Save 10+ Hours Every Week</p>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        No more Monday morning spreadsheets. Settlement reports, P&L breakdowns, and performance
        summaries generate automatically.
      </p>
    </div>
  </Card>
);

const RevenueLeaksCard = () => (
  <Card className="p-0 overflow-hidden group hover:shadow-xl transition-all hover:-translate-y-1 duration-300 border-border/60 flex flex-col">
    <div className="relative p-5 bg-gradient-to-br from-warning/8 via-warning/3 to-transparent overflow-hidden h-[220px] flex flex-col justify-between">
      <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] text-foreground/70 font-mono bg-background/60 px-2 py-0.5 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /> Live
      </div>
      <div className="grid grid-cols-3 gap-2 pt-1">
        {[
          {
            label: 'Tickets',
            value: '$18.2K',
            sub: '82% cap',
            color: 'text-primary',
          },
          {
            label: 'Bar',
            value: '$9.4K',
            sub: '\u2193 12% pace',
            color: 'text-warning',
          },
          {
            label: 'VIP',
            value: '$4.1K',
            sub: '3 tables open',
            color: 'text-success',
          },
        ].map((m) => (
          <div
            key={m.label}
            className="bg-background/80 backdrop-blur-sm rounded-lg p-2.5 border border-border/30 text-center"
          >
            <p className="text-[9px] text-muted-foreground mb-0.5">{m.label}</p>
            <p className={`text-sm font-bold ${m.color}`}>{m.value}</p>
            <p className="text-[8px] text-muted-foreground mt-0.5">{m.sub}</p>
          </div>
        ))}
      </div>
      <div className="flex items-start gap-2 bg-warning/10 border border-warning/20 rounded-lg px-3 py-2">
        <AlertTriangle size={12} className="text-warning flex-shrink-0 mt-0.5" />
        <p className="text-[10px] text-foreground leading-snug">
          <span className="font-semibold">Alert:</span> Bar revenue 12% below pace. Consider drink
          specials or add 1 bartender.
        </p>
      </div>
    </div>
    <div className="p-5 border-t border-border/40 flex-1 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-7 w-7 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
          <TrendingUp size={14} className="text-warning" />
        </div>
        <p className="font-semibold text-sm">Catch Revenue Leaks in Real Time</p>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        See exactly how tonight is tracking before doors close. Spot underperformance early enough
        to act on it.
      </p>
    </div>
  </Card>
);

const BookConfidenceCard = () => (
  <Card className="p-0 overflow-hidden group hover:shadow-xl transition-all hover:-translate-y-1 duration-300 border-border/60 flex flex-col">
    <div className="relative p-5 bg-gradient-to-br from-accent/8 via-accent/3 to-transparent overflow-hidden h-[220px] flex flex-col justify-between">
      <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] bg-accent/15 text-accent px-2 py-0.5 rounded-full font-semibold">
        <Sparkles size={10} /> AI
      </div>
      <div className="space-y-2 pt-1">
        {[
          {
            artist: 'Kaytranada',
            genre: 'Electronic',
            profit: '+$14.2K',
            score: 94,
            rec: true,
          },
          {
            artist: 'Local DJ Night',
            genre: 'Mixed',
            profit: '-$2.1K',
            score: 38,
            rec: false,
          },
          {
            artist: 'Peggy Gou',
            genre: 'House',
            profit: '+$9.8K',
            score: 87,
            rec: true,
          },
        ].map((a) => (
          <div
            key={a.artist}
            className={`flex items-center gap-2.5 rounded-lg px-3 py-2 border ${a.rec ? 'bg-background/80 border-border/30' : 'bg-destructive/5 border-destructive/20'}`}
          >
            <div
              className={`h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-bold ${a.rec ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}
            >
              {a.score}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">{a.artist}</p>
              <p className="text-[9px] text-muted-foreground">{a.genre}</p>
            </div>
            <div className="text-right">
              <p className={`text-xs font-bold ${a.rec ? 'text-success' : 'text-destructive'}`}>
                {a.profit}
              </p>
              <p className="text-[8px] text-muted-foreground">{a.rec ? 'Book' : 'Skip'}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-1">
        <span className="text-[10px] text-muted-foreground">Based on 14 months of data</span>
        <span className="text-[10px] font-bold text-primary">AI-scored</span>
      </div>
    </div>
    <div className="p-5 border-t border-border/40 flex-1 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-7 w-7 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
          <Sparkles size={14} className="text-accent" />
        </div>
        <p className="font-semibold text-sm">Book with Confidence, Not Gut Feel</p>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Know which artists, genres, and nights actually drive profit. Stop repeating expensive
        mistakes.
      </p>
    </div>
  </Card>
);

export default LaunchOutcomesSection;
